import { Link } from 'react-router-dom'

/**
 * Public trust / security overview (linked from marketing footer + Settings).
 * Not a legal instrument — high-level practices and contact for questions.
 */
export function SecurityPage() {
  return (
    <div className="min-h-screen" style={{ background: '#F5F2EA' }}>
      <div className="max-w-xl mx-auto px-4 py-10">
        <Link
          to="/login"
          className="text-xs mb-8 inline-block transition-colors"
          style={{ color: '#9aafb3' }}
        >
          ← Back to sign in
        </Link>

        <header className="mb-10">
          <div className="text-3xl mb-2">🕊</div>
          <h1
            className="text-2xl font-bold tracking-tight"
            style={{ color: '#1a3a40', fontFamily: 'Georgia, serif' }}
          >
            Security &amp; privacy
          </h1>
          <p className="text-sm mt-2" style={{ color: '#3d6068' }}>
            How Duva protects your organization&apos;s data and how to reach us about security.
          </p>
        </header>

        <div
          className="rounded-2xl p-8 space-y-8"
          style={{
            background: '#FDFCF8',
            border: '1px solid #e0d9cc',
            boxShadow: '0 2px 16px rgba(42,122,138,0.07)',
          }}
        >
          <section>
            <h2 className="text-sm font-semibold mb-2" style={{ color: '#1a3a40' }}>
              Encryption &amp; infrastructure
            </h2>
            <p className="text-sm leading-relaxed" style={{ color: '#3d6068' }}>
              Traffic between users, Slack/Teams, and our API uses <strong style={{ color: '#1a3a40' }}>TLS</strong>{' '}
              in transit. Application data is stored in{' '}
              <strong style={{ color: '#1a3a40' }}>Cloudflare D1</strong> (encrypted at rest as provided by the
              platform). We do not operate our own data-center footprint for core product data.
            </p>
          </section>

          <section>
            <h2 className="text-sm font-semibold mb-2" style={{ color: '#1a3a40' }}>
              Authentication
            </h2>
            <p className="text-sm leading-relaxed" style={{ color: '#3d6068' }}>
              Duva does <strong style={{ color: '#1a3a40' }}>not store user passwords</strong>. Sign-in is
              delegated to <strong style={{ color: '#1a3a40' }}>Slack</strong>,{' '}
              <strong style={{ color: '#1a3a40' }}>Microsoft Teams</strong>, or your corporate{' '}
              <strong style={{ color: '#1a3a40' }}>SSO / OIDC</strong> provider (Enterprise). Access tokens are
              handled according to each integration&apos;s standard OAuth/OIDC flows.
            </p>
          </section>

          <section>
            <h2 className="text-sm font-semibold mb-2" style={{ color: '#1a3a40' }}>
              Compliance-oriented features
            </h2>
            <ul className="text-sm space-y-2" style={{ color: '#3d6068' }}>
              <li>
                <strong style={{ color: '#1a3a40' }}>GDPR:</strong> Users can request erasure of personal data from
                the in-app Settings (danger zone). Some organization-level records may be retained where required
                for legitimate business or legal purposes.
              </li>
              <li>
                <strong style={{ color: '#1a3a40' }}>Audit trail:</strong> Security-sensitive mutations are logged
                with actor and timestamp to support accountability and incident review.
              </li>
              <li>
                <strong style={{ color: '#1a3a40' }}>Enterprise:</strong> SSO secrets are stored encrypted at rest;
                we do not log SSO client secrets.
              </li>
            </ul>
          </section>

          <section id="what-we-measure">
            <h2 className="text-sm font-semibold mb-2" style={{ color: '#1a3a40' }}>
              What we measure (for buyers &amp; admins)
            </h2>
            <p className="text-sm leading-relaxed mb-3" style={{ color: '#3d6068' }}>
              Duva is built around <strong style={{ color: '#1a3a40' }}>delivery and engagement signals</strong>, not
              surveillance of message content. Typical aggregates include:
            </p>
            <ul className="text-sm space-y-2 list-disc pl-5" style={{ color: '#3d6068' }}>
              <li>
                <strong style={{ color: '#1a3a40' }}>Nudge delivery volume</strong> — counts of sends over time (per org),
                so you can confirm the system is reaching people.
              </li>
              <li>
                <strong style={{ color: '#1a3a40' }}>Open and action rates</strong> — aggregated per nudge template
                (e.g. Done / snooze), shown in Analytics; suitable for screenshots or exec summaries.
              </li>
              <li>
                <strong style={{ color: '#1a3a40' }}>Engagement score &amp; trends</strong> — derived summaries over
                rolling windows to show momentum; optional AI narrative for admins where enabled.
              </li>
              <li>
                <strong style={{ color: '#1a3a40' }}>Optional pulses</strong> — anonymized aggregates when enough people
                respond (e.g. leadership pulse), never individual answers in team-facing views.
              </li>
            </ul>
            <p className="text-sm leading-relaxed mt-3" style={{ color: '#3d6068' }}>
              Shareable HTML reports (when generated from the dashboard) summarize these metrics for stakeholders without
              granting dashboard access.
            </p>
          </section>

          <section>
            <h2 className="text-sm font-semibold mb-2" style={{ color: '#1a3a40' }}>
              Certifications
            </h2>
            <p className="text-sm leading-relaxed" style={{ color: '#3d6068' }}>
              Enterprise customers may request <strong style={{ color: '#1a3a40' }}>SOC 2–oriented documentation</strong>{' '}
              and answers to security questionnaires as part of procurement. We do not publicly host a full SOC 2
              report on this page.
            </p>
          </section>

          <section>
            <h2 className="text-sm font-semibold mb-2" style={{ color: '#1a3a40' }}>
              Report a vulnerability
            </h2>
            <p className="text-sm leading-relaxed" style={{ color: '#3d6068' }}>
              If you believe you&apos;ve found a security issue, please email{' '}
              <a
                href="mailto:security@getduva.com?subject=Security%20report"
                className="font-medium underline underline-offset-2"
                style={{ color: '#2A7A8A' }}
              >
                security@getduva.com
              </a>
              . Include steps to reproduce and, if possible, impact assessment. We appreciate responsible disclosure
              and will work with you on a coordinated response.
            </p>
          </section>

          <p className="text-xs pt-2 border-t" style={{ color: '#9aafb3', borderColor: '#e0d9cc' }}>
            This page is a summary for customers and prospects. For privacy practices see our{' '}
            <Link to="/privacy" className="underline underline-offset-2" style={{ color: '#2A7A8A' }}>
              Privacy
            </Link>{' '}
            page. Contractual terms are in your order form / agreement.
          </p>
        </div>
      </div>
    </div>
  )
}
